import React, { memo, useMemo } from "react";
import { valueFormat } from "src/helper/valueFormat";
import { convertTaxAndFee, convertTip } from "src/helper/tips";
import { ORDER_TYPE_KEYS_DELIVERY, filteredData } from "src/utils";
import { convertPaymentMethod } from "src/utils/paymentUtils";
import { calculateTotalSurcharge } from "src/helper/calculateTotalSurcharge";

//TODO: Need to add the Material UI components for the OrderCharges component

const OrderCharges = ({ orderData }) => {
  const orderDiscountAmount = useMemo(() => {
    return orderData?.charges?.discountDetails?.reduce((acc, item) => {
      if (item?.couponId) return acc; // skip this coupon
      return acc + item?.discountAmount;
    }, 0);
  }, [orderData?.charges?.discountDetails]);

  const couponDiscountAmount = useMemo(() => {
    return orderData?.charges?.discountDetails?.find((item) => item?.couponId);
  }, [orderData?.charges?.discountDetails]);

  // A coupon is carried by the promotion its code unlocked. The summary keeps
  // the amount only; the promotion name is shown on the item it applied to.
  const couponPromotions = useMemo(() => {
    return (orderData?.charges?.promotionDetails || [])
      .filter((detail) => !!detail?.promotionCode)
      .map((detail) => ({
        code: detail.promotionCode,
        amount: Math.abs(Number(detail?.discountAmount || 0)),
      }));
  }, [orderData?.charges?.promotionDetails]);

  const promotionAmount = useMemo(() => {
    const total = couponPromotions.reduce(
      (rest, coupon) => rest - coupon.amount,
      Number(orderData?.charges?.promotionDiscount || 0),
    );
    return Math.round(total * 100) / 100;
  }, [orderData?.charges?.promotionDiscount, couponPromotions]);

  // Extract tender information from transactions
  const tenderInfo = useMemo(() => {
    const transactions = orderData?.metadata?.transactions;
    if (!transactions?.length)
      return { tenders: [], totalChange: 0, totalSurcharge: 0 };

    const filtered = filteredData(transactions);
    const tenderMap = new Map(); // Use Map to group by tender type
    let totalChange = 0;
    let totalSurcharge = 0;

    filtered.forEach((item) => {
      const amount = item?.paymentInfo?.tendered || item?.tendered || 0;
      const tender = convertPaymentMethod(item?.tender?.method) || "";
      const changed = item?.paymentInfo?.changed || item?.changed || 0;
      const surchargeAmount = item?.surchargeAmount || 0;

      if (tender && amount > 0) {
        // Group by tender type and sum amounts
        if (tenderMap.has(tender)) {
          tenderMap.set(tender, tenderMap.get(tender) + Number(amount));
        } else {
          tenderMap.set(tender, Number(amount));
        }
      }

      if (changed > 0) {
        totalChange += Number(changed);
      }

      if (surchargeAmount > 0) {
        totalSurcharge += Number(surchargeAmount);
      }
    });

    // Convert Map to array of tender objects
    const tenders = Array.from(tenderMap.entries()).map(([tender, amount]) => ({
      tender,
      amount,
    }));

    return { tenders, totalChange, totalSurcharge };
  }, [orderData?.metadata?.transactions]);

  const totalRewardAmountApplied = useMemo(() => {
    if (orderData?.charges?.totalRewardAmountApplied)
      return orderData?.charges?.totalRewardAmountApplied;
    return tenderInfo?.tenders
      .filter(
        (tender) =>
          tender?.tender && tender?.tender?.toLowerCase().includes("reward"),
      )
      .map((tender) => tender?.amount)
      .reduce((acc, curr) => acc + curr, 0);
  }, [tenderInfo?.tenders]);

  if (!orderData?.charges?.selectedRewardAmount) {
    orderData.charges.selectedRewardAmount =
      Number(orderData?.charges?.rewardAmount) -
      Number(totalRewardAmountApplied || 0);
  }

  console.log(tenderInfo);

  return (
    <>
      <tr className="totals-price-size">
        <td className="uppercase">Subtotal:</td>
        <td className="totals-price-size">
          {valueFormat(`$${Number(orderData?.charges?.subTotal).toFixed(2)}`)}
        </td>
      </tr>
      {orderData?.charges?.compAmount ? (
        <tr className="totals-price-size">
          <td className="uppercase">Comp:</td>
          <td className="totals-price-size">
            {valueFormat(
              `($${Number(orderData?.charges?.compAmount).toFixed(2)})`,
            )}
          </td>
        </tr>
      ) : (
        <></>
      )}

      {orderDiscountAmount > 0 ? (
        <tr className="totals-price-size">
          <td className="uppercase">Discount:</td>
          <td>
            {valueFormat(`($${Number(orderDiscountAmount || 0)?.toFixed(2)})`)}
          </td>
        </tr>
      ) : (
        <></>
      )}
      {couponDiscountAmount?.discountAmount > 0 ? (
        <tr className="totals-price-size">
          <td className="uppercase">Coupon:</td>
          <td>
            {valueFormat(
              `($${Number(couponDiscountAmount?.discountAmount || 0)?.toFixed(2)})`,
            )}
          </td>
        </tr>
      ) : (
        <></>
      )}
      {couponPromotions.map((coupon) => (
        <tr className="totals-price-size" key={coupon.code}>
          <td className="uppercase">Coupon:</td>
          <td>{valueFormat(`($${coupon.amount.toFixed(2)})`)}</td>
        </tr>
      ))}
      {promotionAmount > 0 ? (
        <tr className="totals-price-size">
          <td className="uppercase">Promotions:</td>
          <td>
            {valueFormat(`($${Number(promotionAmount || 0)?.toFixed(2)})`)}
          </td>
        </tr>
      ) : (
        <></>
      )}
      {ORDER_TYPE_KEYS_DELIVERY.includes(orderData?.orderType) ? (
        <tr className="totals-price-size">
          <td className="uppercase">Delivery Fee:</td>
          <td>
            {valueFormat(
              `$${Number(orderData?.charges?.deliveryFee).toFixed(2)}`,
            )}
          </td>
        </tr>
      ) : (
        <></>
      )}
      <>
        {orderData?.charges?.tipInfo?.driver > 0 ||
        orderData?.charges?.tipInfo?.kitchen > 0 ? (
          <tr className="totals-price-size">
            <td className="uppercase">Tips:</td>
            <td>
              {valueFormat(
                `$${Number(
                  (orderData?.charges?.tipInfo?.driver || 0) +
                    (orderData?.charges?.tipInfo?.kitchen || 0),
                ).toFixed(2)}`,
              )}
            </td>
          </tr>
        ) : null}
        {orderData.charges?.tipInfo?.kitchen &&
        orderData.charges?.tipInfo?.kitchen &&
        orderData.charges?.tipInfo?.kitchen > 0 ? (
          <tr
            className="tip totals-price-size delivery-wrapper"
            style={{ paddingLeft: "32px" }}
          >
            <td className="uppercase">Kitchen Tip:</td>
            <td style={{ fontSize: "16px", paddingRight: "100px" }}>
              {convertTip(orderData.charges?.tipInfo?.kitchen || 0)}
            </td>
          </tr>
        ) : null}

        {ORDER_TYPE_KEYS_DELIVERY.includes(orderData?.orderType) &&
        orderData.charges?.tipInfo?.driver &&
        orderData.charges?.tipInfo?.driver > 0 ? (
          <tr
            className="tip totals-price-size delivery-wrapper"
            style={{ paddingLeft: "32px" }}
          >
            <td className="uppercase">Driver Tip:</td>
            <td style={{ fontSize: "16px", paddingRight: "100px" }}>
              {convertTip(orderData.charges?.tipInfo?.driver || 0)}
            </td>
          </tr>
        ) : null}
      </>
      <tr className="totals-price-size uppercase">
        <td>Taxes and Fees:</td>
        <td>
          {convertTaxAndFee(
            orderData?.charges?.servicingFee,
            orderData?.charges?.taxAmount,
          )}
        </td>
      </tr>
      <tr
        className="totals-price-size uppercase delivery-wrapper"
        style={{ paddingLeft: "32px", fontSize: "16px" }}
      >
        <td className="" style={{ fontSize: "16px" }}>
          Tax:
        </td>
        <td className="" style={{ fontSize: "16px", paddingRight: "100px" }}>
          {valueFormat(
            `$${Number(orderData?.charges?.taxAmount || 0).toFixed(2)}`,
          )}
        </td>
      </tr>
      {orderData?.charges?.servicingFee?.processingFee ||
      orderData?.charges?.servicingFee?.marketplaceFee ||
      orderData?.charges?.servicingFee?.ordersCoFee ? (
        <tr
          className="totals-price-size uppercase delivery-wrapper"
          style={{ paddingLeft: "32px", fontSize: "16px" }}
        >
          <td className="delivery-wrapper-left" style={{ fontSize: "16px" }}>
            Processing Fee:
          </td>
          <td className="delivery-wrapper-right" style={{ fontSize: "16px" }}>
            {valueFormat(
              `$${Number(
                (orderData?.charges?.servicingFee?.processingFee || 0) +
                  (orderData?.charges?.servicingFee?.marketplaceFee || 0) +
                  (orderData?.charges?.servicingFee?.ordersCoFee || 0),
              ).toFixed(2)}`,
            )}
          </td>
        </tr>
      ) : (
        ""
      )}
      {orderData?.charges?.selectedRewardAmount > 0 ? (
        <tr className="totals-price-size uppercase">
          <td>Reward Selected:</td>
          <td>
            {valueFormat(
              `($${Number(orderData?.charges?.selectedRewardAmount || 0).toFixed(2)})`,
            )}
          </td>
        </tr>
      ) : (
        <></>
      )}

      <tr className="totals-price-size uppercase">
        <td>Total:</td>
        <td>
          {valueFormat(`$${Number(orderData?.charges?.total || 0).toFixed(2)}`)}
        </td>
      </tr>
      {calculateTotalSurcharge(orderData?.metadata?.transactions) > 0 ? (
        <tr className="totals-price-size">
          <td className="uppercase">Surcharge:</td>
          <td className="totals-price-size">
            {valueFormat(
              `$${calculateTotalSurcharge(orderData?.metadata?.transactions)}`,
            )}
          </td>
        </tr>
      ) : null}
      {orderData?.metadata?.transactions?.length > 0 ? (
        <hr className="line-header" style={{ marginBottom: "12px" }} />
      ) : null}
      {/* Add tenders after Total */}
      {tenderInfo.tenders.map((tender, index) => (
        <tr key={index} className="totals-price-size">
          <td>Tender {tender?.tender}:</td>
          <td>{valueFormat(`($${tender.amount.toFixed(2)})`)}</td>
        </tr>
      ))}
      {tenderInfo.totalSurcharge > 0 && (
        <tr className="totals-price-size uppercase">
          <td>Surcharge:</td>
          <td>{valueFormat(`$${tenderInfo?.totalSurcharge.toFixed(2)}`)}</td>
        </tr>
      )}
      {/* Add change line before Balance Due */}
      {tenderInfo.totalChange > 0 && (
        <tr className="totals-price-size uppercase">
          <td>Change:</td>
          <td>{valueFormat(`$${tenderInfo.totalChange.toFixed(2)}`)}</td>
        </tr>
      )}
      <hr className="line-header" style={{ marginTop: "12px" }} />
      <tr className="uppercase global-total">
        <td
          style={{
            color:
              (orderData?.charges?.balanceDue ?? 0) < 0 ? "red" : "initial",
          }}
        >
          {" "}
          {(orderData?.charges?.balanceDue ?? 0) < 0
            ? "Refund Due:"
            : "Balance Due:"}
        </td>
        <td
          style={{
            color:
              (orderData?.charges?.balanceDue ?? 0) < 0 ? "red" : "initial",
          }}
        >
          {valueFormat(
            `$${Number(Math.abs(orderData?.charges?.balanceDue) || 0).toFixed(2)}`,
          )}
        </td>
      </tr>
    </>
  );
};
export default memo(OrderCharges);
